import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '@/contexts/UserContext';
import type { ChartData, ChartOptions } from 'chart.js';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Pie, Doughnut, Bar } from 'react-chartjs-2';
import { useProject } from '@/components/ProjectSelector';
import { fetchInvoicesDashboard, type DashboardData } from '../services/invoicesDashboardService';

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

export function InvoicesDashboard() {
  const { companyId } = useUser();
  const { selectedProject } = useProject();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const responsiveNumberStyle = { fontSize: 'clamp(1.5rem, 3vw, 1.75rem)' };

  const formatCurrency = (value: number | string) => {
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return '$0.00';
    }
    return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  useEffect(() => {
    if (!companyId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchInvoicesDashboard(selectedProject, controller.signal)
      .then((dashboardData) => setData(dashboardData))
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('Error fetching the invoices dashboard:', error);
        setData(null);
        setError(error instanceof Error && error.message ? error.message : 'The dashboard could not be loaded.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [companyId, selectedProject, attempt]);

  // Chart.js/canvas fillStyle cannot resolve CSS custom properties (var(--x)
  // strings fail to parse as canvas colors and silently no-op), so these stay
  // literal hex even though most exactly match design-system palette entries
  // (blue-500, emerald-500, amber-500, red-500, cyan-500, orange-500 --
  // #8B5CF6/#84CC16 have no palette match at all). Gap: would need a JS-side
  // (non-CSS-var) constants source to centralize -- out of scope here.
  const pieColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

  const chartOptions = {
    pieWithLegend: {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.parsed || 0;
              const percentage = context.raw?.percentage ?? context.raw?.count ?? null;
              if (typeof percentage === 'number') {
                return `${label}: ${formatCurrency(value)} (${percentage.toFixed(1)}%)`;
              }
              return `${label}: ${formatCurrency(value)}`;
            }
          }
        }
      }
    } satisfies ChartOptions<'pie'>,
    doughnut: {
      responsive: true,
      cutout: '60%',
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const count = context.parsed || 0;
              const percentage = context.raw?.percentage ?? 0;
              return `${label}: ${count} invoices (${percentage.toFixed(1)}%)`;
            }
          }
        }
      }
    } satisfies ChartOptions<'doughnut'>,
    bar: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context: any) => formatCurrency(context.parsed.y)
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 0,
            minRotation: 0
          }
        },
        y: {
          grid: { color: '#D1D5DB' /* matches --gray-300; kept literal, see pieColors comment above re: canvas + CSS vars */ },
          ticks: {
            callback: value => {
              const numericValue = typeof value === 'number' ? value : Number(value);
              return Number.isFinite(numericValue) ? formatCurrency(numericValue) : `${value}`;
            }
          }
        }
      }
    } satisfies ChartOptions<'bar'>,
    agingBar: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context: any) => formatCurrency(context.parsed.y)
          }
        }
      },
      scales: {
        x: {
          grid: { display: false }
        },
        y: {
          grid: { color: '#D1D5DB' /* matches --gray-300; kept literal, see pieColors comment above re: canvas + CSS vars */ },
          ticks: {
            callback: value => {
              const numericValue = typeof value === 'number' ? value : Number(value);
              return Number.isFinite(numericValue) ? formatCurrency(numericValue) : `${value}`;
            }
          }
        }
      }
    } satisfies ChartOptions<'bar'>
  };

  const chartSeries = useMemo(() => {
    if (!data) {
      return {
        outstandingByAmount: { labels: [], datasets: [] },
        openInvoicesByProject: { labels: [], datasets: [] },
        cashCollected: { labels: [], datasets: [] },
        remainingExposure: { labels: [], datasets: [] },
        arAging: { labels: [], datasets: [] }
      };
    }

    // Add null checks for all data arrays
    const outstandingByAmount = data.outstandingByAmount || [];
    const openInvoicesByProject = data.openInvoicesByProject || [];
    const cashCollected = data.cashCollected || [];
    const arAging = data.arAging || [];
    const remainingExposureByProject = data.remainingExposureByProject || [];

    const outstandingLabels = outstandingByAmount.map(item => item.project);
    const openCountLabels = openInvoicesByProject.map(item => item.project);
    const cashCollectedLabels = cashCollected.map(entry => entry.month);
    const cashCollectedValues = cashCollected.map(entry => entry.amount);
    const agingLabels = arAging.map(entry => entry.range);
    const agingValues = arAging.map(entry => entry.amount);
    const remainingExposureValues = remainingExposureByProject.map(item => item.remaining);

    return {
      outstandingByAmount: {
        labels: outstandingLabels,
        datasets: [
          {
            label: 'Outstanding Balance',
            data: outstandingByAmount.map(item => ({ value: Number(item.amount.toFixed(2)), percentage: item.percentage })),
            parsing: {
              key: 'value'
            },
            backgroundColor: outstandingLabels.map((_, index) => pieColors[index % pieColors.length])
          }
        ]
      } as ChartData<'pie', { value: number; percentage: number }[], string>,
      remainingExposure: {
        labels: remainingExposureByProject.map(item => item.project),
        datasets: [
          {
            label: 'Unbilled Contract Value',
            data: remainingExposureValues,
            backgroundColor: '#6366F1', /* matches --indigo-500; kept literal, see pieColors comment re: canvas + CSS vars */
            borderRadius: 6
          }
        ]
      } as ChartData<'bar'>,
      openInvoicesByProject: {
        labels: openCountLabels,
        datasets: [
          {
            label: 'Open Invoice Count',
            data: openInvoicesByProject.map(item => ({ value: item.count, percentage: item.percentage })),
            parsing: {
              key: 'value'
            },
            backgroundColor: openCountLabels.map((_, index) => pieColors[index % pieColors.length])
          }
        ]
      } as ChartData<'doughnut', { value: number; percentage: number }[], string>,
      cashCollected: {
        labels: cashCollectedLabels,
        datasets: [
          {
            label: 'Payments Received',
            data: cashCollectedValues,
            backgroundColor: '#3B82F6', /* matches --blue-500; kept literal, see pieColors comment re: canvas + CSS vars */
            borderRadius: 6
          }
        ]
      } as ChartData<'bar'>,
      arAging: {
        labels: agingLabels,
        datasets: [
          {
            label: 'Outstanding Balance',
            data: agingValues,
            backgroundColor: '#F59E0B', /* matches --amber-500; kept literal, see pieColors comment re: canvas + CSS vars */
            borderRadius: 6
          }
        ]
      } as ChartData<'bar'>
    };
  }, [data]);

  // The page keeps its shape while the figures are on their way: the four KPI cards are there from the start as placeholders
  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {['Total Outstanding', 'Paid This Month', 'Overdue Invoices', 'Draft Invoices'].map((label) => (
            <div key={label} className="bg-white p-6 rounded-lg shadow-md border border-gray-200" data-testid="kpi-skeleton">
              <h3 className="text-sm font-medium text-gray-500">{label}</h3>
              <div className="mt-2 h-7 w-24 animate-pulse rounded bg-gray-200" aria-label="Loading" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-64 gap-3" role="alert">
        <div className="text-red-600">Could not load the invoices dashboard.</div>
        <div className="text-sm text-gray-500">{error}</div>
        <button type="button" className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50" onClick={() => setAttempt((n) => n + 1)}>Retry</button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Total Outstanding</h3>
          <p className="font-bold text-gray-900 leading-tight break-words" style={responsiveNumberStyle}>{formatCurrency(data.totalOutstanding)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Paid This Month</h3>
          <p className="font-bold text-green-600 leading-tight break-words" style={responsiveNumberStyle}>{formatCurrency(data.paidThisMonth)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Overdue Invoices</h3>
          <p className="font-bold text-red-600 leading-tight break-words" style={responsiveNumberStyle}>{data.overdueInvoices}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Draft Invoices</h3>
          <p className="font-bold text-yellow-600 leading-tight break-words" style={responsiveNumberStyle}>{data.draftInvoices}</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Outstanding by Amount */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Outstanding by Project</h3>
          <div className="h-[25rem] md:h-[31.25rem]">
            <Pie data={chartSeries.outstandingByAmount} options={chartOptions.pieWithLegend} />
          </div>
        </div>

        {/* Open Invoices by Project */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Open Invoices by Project</h3>
          <div className="h-[25rem] md:h-[31.25rem]">
            <Doughnut data={chartSeries.openInvoicesByProject} options={chartOptions.doughnut} />
          </div>
        </div>
      </div>

      {/* Cash Collected & Aging */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Cash Collected (Last 6 Months)</h3>
          <div className="h-80">
            <Bar data={chartSeries.cashCollected} options={chartOptions.bar} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Unbilled Contract Value</h3>
          <div className="h-80">
            <Bar data={chartSeries.remainingExposure} options={chartOptions.bar} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">AR Aging Buckets</h3>
          <div className="h-80">
            <Bar data={chartSeries.arAging} options={chartOptions.agingBar} />
          </div>
        </div>
      </div>

      {/* Top Clients */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Fastest Paying Clients</h3>
          <div className="space-y-2">
            {(data.topClients?.fastestPaying || []).map((client, index) => (
              <div key={client.client} className="flex justify-between items-center">
                <span className="text-sm">{client.client}</span>
                <span className="text-sm text-green-600">{client.avgDays.toFixed(0)} days</span>
              </div>
            ))}
            {(data.topClients?.fastestPaying || []).length === 0 && (
              <div className="text-center text-gray-500 py-4">No payment data available</div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Largest Outstanding Clients</h3>
          <div className="space-y-2">
            {(data.topClients?.largestOutstanding || []).map((client, index) => (
              <div key={client.client} className="flex justify-between items-center">
                <span className="text-sm">{client.client}</span>
                <span className="text-sm text-red-600">{formatCurrency(client.amount)}</span>
              </div>
            ))}
            {(data.topClients?.largestOutstanding || []).length === 0 && (
              <div className="text-center text-gray-500 py-4">No outstanding amounts</div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Top Billed Clients</h3>
          <div className="space-y-2">
            {(data.topClients?.highestPaid || []).map((client, index) => (
              <div key={client.client} className="flex justify-between items-center">
                <span className="text-sm">{client.client}</span>
                <span className="text-sm text-blue-600">{formatCurrency(client.amount)}</span>
              </div>
            ))}
            {(data.topClients?.highestPaid || []).length === 0 && (
              <div className="text-center text-gray-500 py-4">No payment data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming Payments */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Upcoming Payments</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Invoice</th>
                <th className="text-left py-2">Project</th>
                <th className="text-right py-2">Amount</th>
                <th className="text-right py-2">Days Until Payment</th>
              </tr>
            </thead>
            <tbody>
              {(data.upcomingPayments || []).slice(0, 10).map((payment, index) => (
                <tr key={index} className="border-b">
                  <td className="py-2">{payment.invoice}</td>
                  <td className="py-2">{payment.project}</td>
                  <td className="text-right py-2">{formatCurrency(payment.amount)}</td>
                  <td className="text-right py-2">{payment.daysUntilPayment} days</td>
                </tr>
              ))}
              {(data.upcomingPayments || []).length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-gray-500 py-8">No upcoming payments</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Status */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Invoice Status Breakdown</h3>
        <div className="flex items-center space-x-4">
          {(data.invoiceStatus || []).map((status, index) => (
            <div key={status.status} className="flex items-center">
              <div 
                className="w-4 h-4 rounded-full mr-2"
                style={{ backgroundColor: `hsl(${index * 120}, 70%, 50%)` }}
              />
              <span className="text-sm">{status.status}: {status.count}</span>
            </div>
          ))}
          {(data.invoiceStatus || []).length === 0 && (
            <div className="text-gray-500">No invoice status data available</div>
          )}
        </div>
      </div>
    </div>
  );
}